/**
 * useCrmAuth — контекст авторизации CRM-пользователей.
 * Аутентификация идёт через таблицу crm_users.
 */

import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import {
  crmLogin,
  crmLogout,
  isCrmAuthed,
  isCrmAuthedSync,
  getCrmCurrentUser,
  type CrmSessionUser,
} from '../lib/crmAuth';

interface CrmAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  currentUser: CrmSessionUser | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  recheck: () => Promise<void>;
}

const CrmAuthContext = createContext<CrmAuthContextType | undefined>(undefined);

export function CrmAuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(isCrmAuthedSync());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentUser, setCurrentUser] = useState<CrmSessionUser | null>(getCrmCurrentUser());

  const recheck = useCallback(async () => {
    const authed = await isCrmAuthed();
    setIsAuthenticated(authed);
    setCurrentUser(getCrmCurrentUser());
  }, []);

  useEffect(() => {
    let alive = true;
    isCrmAuthed().then((authed) => {
      if (alive) {
        setIsAuthenticated(authed);
        setCurrentUser(getCrmCurrentUser());
        setIsLoading(false);
      }
    });
    return () => { alive = false; };
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    const user = await crmLogin(username, password);
    setIsAuthenticated(true);
    setCurrentUser(user);
  };

  const logout = useCallback((): void => {
    crmLogout();
    setIsAuthenticated(false);
    setCurrentUser(null);
  }, []);

  return (
    <CrmAuthContext.Provider value={{ isAuthenticated, isLoading, currentUser, login, logout, recheck }}>
      {children}
    </CrmAuthContext.Provider>
  );
}

export function useCrmAuth(): CrmAuthContextType {
  const ctx = useContext(CrmAuthContext);
  if (!ctx) throw new Error('useCrmAuth must be used within CrmAuthProvider');
  return ctx;
}
